import {
  Applicant,
  DocumentReviewEntry,
  Entry,
  EntryIncomeCalculation,
  EntryReport,
  EntryReportData,
  EntryRequest,
  EntryRequestDocumentsRequest,
  EntryReviewEscalationRequest,
  FDEInboxEntryEntries,
  FDEInboxEntryIncomeCalculation,
  Folder,
  IncomeCalculationStatusDetail,
  MatchingEntry
} from '@snapptinc/fraud-platform/api';
import { EntryResultEnum, EntryStatusEnum, EntrySuggestedRulingEnum } from 'src/ts/enums';

import { IAuthenticatedUser } from 'src/ts/interfaces/user';

export type EntryRequestType = EntryRequest;
export type EntryRequestDocumentsRequestType = EntryRequestDocumentsRequest;
export type EntryReviewEscalationRequestType = EntryReviewEscalationRequest;

export type EntryResultType = (typeof EntryResultEnum)[keyof typeof EntryResultEnum];
export type EntryStatusType = (typeof EntryStatusEnum)[keyof typeof EntryStatusEnum];
export type EntrySuggestedRulingType =
  (typeof EntrySuggestedRulingEnum)[keyof typeof EntrySuggestedRulingEnum];

export const IncomeVerificationSourceType = {
  CONNECTED_PAYROLL: 'CONNECTED_PAYROLL',
  DOCUMENT_UPLOAD: 'DOCUMENT_UPLOAD'
} as const;

export type IncomeVerificationSourceTypeValue =
  (typeof IncomeVerificationSourceType)[keyof typeof IncomeVerificationSourceType];

export interface IVDisputableMetadata {
  source_type: IncomeVerificationSourceTypeValue;
}

export interface IEntry extends Omit<Entry, 'income_verification_disputable'> {
  // TODO: Update missing properties on the EntrySchema
  id: string;
  result: EntryResultType;
  status: EntryStatusType;
  suggested_ruling: EntrySuggestedRulingType;
  applicant: Partial<Applicant>;
  folder: Partial<Folder>;
  reviewer?: Partial<IAuthenticatedUser>;
  note?: string;
  workflow?: 'income_verification' | 'specialty_income' | 'asset_verification';
  income_verification_review_id?: string;
  specialty_review_item_id?: string;
  // Hand-widened for VOA (ITL-2996/ITL-2997) — SDK frozen; mirrors the
  // specialty_review_item_id precedent above.
  asset_verification_review_item_id?: string;
  document_reviews?: [] | DocumentReviewEntry[];
  // Override SDK boolean — backend now returns object with source_type for frontend gating (ITL-2473)
  income_verification_disputable?: IVDisputableMetadata | null;
  // View-layer virtual field stamped by SnapptWeb.EntryView (:private scope),
  // NOT part of any OpenAPI schema — hand-added, will not appear on SDK
  // regeneration. True when an OL-only entry has an offer letter in scalar
  // PENDING_EMPLOYER_VERIFICATION; surfaces the admin income-override button
  // for entries whose income is terminally Pending.
  offer_letter_pending_employer_verification?: boolean;
  // View-layer virtual field stamped by SnapptWeb.EntryView (:private scope),
  // NOT part of any OpenAPI schema. True while an active :specialty_income
  // ReviewItem exists for the entry — the folder page then shows income as
  // Pending and surfaces a "Pending Specialty Review" note (ITL-2708).
  pending_specialty_review?: boolean;
}

export interface IMatchingEntry extends Omit<MatchingEntry, 'entry_id'> {
  entry_id: string;
}

export type IEntryReportData = EntryReportData;
export type IEntryReport = EntryReport;
export type IFDEInboxEntryEntries = FDEInboxEntryEntries & {
  applicant_first_name?: string;
  applicant_last_name?: string;
  // ITL-2809: per-entry leasing-agent "Request Employment Verification"
  // eligibility, computed server-side in `Snappt.FDEInboxes`. Absent when
  // the property's `offer_letter_verification` PropertyFeature is OFF
  // (the FE then renders nothing). Present means the menu item shows;
  // `enabled: false` renders disabled with `disabled_reason` as tooltip.
  //
  // This is a view-layer virtual field (stamped by `SnapptWeb.FDEInboxView`),
  // NOT part of any OpenAPI schema — it is hand-added here and will not appear
  // on SDK regeneration. `disabled_reason` is non-optional: when the object is
  // present the server always serializes the key (a string, or `null` when
  // `enabled` is true), so there is no `undefined` at the wire level.
  employment_verification?: {
    enabled: boolean;
    disabled_reason: string | null;
  };
  // ITL-2900: view-layer virtual field stamped by `SnapptWeb.FDEInboxView`,
  // NOT part of any OpenAPI schema. True while an active :specialty_income
  // ReviewItem exists for the entry — the inbox row then withholds the
  // income value and shows "Pending", mirroring the folder page.
  pending_specialty_review?: boolean;
};
export type IFDEInboxEntryIncomeCalculation = FDEInboxEntryIncomeCalculation;
export type IIncomeCalculationStatusDetails = Array<IncomeCalculationStatusDetail> | null;
export type IEntryIncomeCalculation = EntryIncomeCalculation;
